'use client';

import React, { forwardRef } from 'react';
import { cn } from '@/lib/cn';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    label?: string;
    error?: string;
    icon?: React.ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
    ({ label, error, icon, className = '', id, required, children, ...props }, ref) => {
        const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');
        return (
            <div className="space-y-1.5">
                {label && (
                    <label htmlFor={selectId} className="block text-xs text-zinc-400 font-medium">
                        {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
                    </label>
                )}
                <div className="relative">
                    {icon && (
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-zinc-500">
                            {icon}
                        </div>
                    )}
                    <select
                        ref={ref}
                        id={selectId}
                        required={required}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? `${selectId}-error` : undefined}
                        className={cn(
                            'w-full bg-bg-surface border rounded-xl px-4 py-3',
                            'text-sm text-white',
                            'transition-colors duration-150',
                            'focus:outline-none focus:border-border-strong focus:ring-1 focus:ring-zinc-500/30',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                            'appearance-none',
                            error ? 'border-rose-500/50' : 'border-border-default',
                            icon ? 'pl-10' : '',
                            className,
                        )}
                        {...props}
                    >
                        {children}
                    </select>
                    {/* Chevron indicator */}
                    <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-zinc-500">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
                {error && (
                    <p id={`${selectId}-error`} className="text-xs text-rose-400" role="alert">{error}</p>
                )}
            </div>
        );
    }
);

Select.displayName = 'Select';
